import { Box, Chip, IconButton, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import { CameraAlt, Download, List, TouchApp, ViewComfy, GridView } from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import type { PhotoViewMode } from '../../../types/photo';

interface PhotoToolbarProps {
  viewMode: PhotoViewMode;
  onViewModeChange: (mode: PhotoViewMode) => void;
  filledCount: number;
  totalSlots: number;
  onDownloadAll: () => void;
  hasPhotos: boolean;
  onOpenUploadGuide?: () => void;
  onOpenActionsGuide?: () => void;
}

export const PhotoToolbar = ({
  viewMode, onViewModeChange, filledCount, totalSlots, onDownloadAll, hasPhotos,
  onOpenUploadGuide, onOpenActionsGuide,
}: PhotoToolbarProps) => {
  const theme = useTheme();

  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      mb: 2, gap: 2, flexWrap: 'wrap',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Chip
          label={`${filledCount} / ${totalSlots} photos`}
          size="small"
          sx={{
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
            fontWeight: 600,
            fontSize: '0.8rem',
          }}
        />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={viewMode}
          onChange={(_, v) => v && onViewModeChange(v as PhotoViewMode)}
          sx={{ '& .MuiToggleButton-root': { px: 1.2, py: 0.5 } }}
        >
          <ToggleButton value="medium"><Tooltip title="Stage Grid"><GridView fontSize="small" /></Tooltip></ToggleButton>
          <ToggleButton value="large"><Tooltip title="Gallery"><ViewComfy fontSize="small" /></Tooltip></ToggleButton>
          <ToggleButton value="list"><Tooltip title="List"><List fontSize="small" /></Tooltip></ToggleButton>
        </ToggleButtonGroup>

        <Tooltip title="Download all as ZIP">
          <span>
            <IconButton size="small" onClick={onDownloadAll} disabled={!hasPhotos}>
              <Download fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Box sx={{ display: 'flex', alignItems: 'center', ml: 0.5, pl: 1, borderLeft: `1px solid ${alpha(theme.palette.divider, 0.5)}` }}>
          {onOpenUploadGuide && (
            <Tooltip title="How to take & upload photos">
              <IconButton size="small" onClick={onOpenUploadGuide}
                sx={{ color: alpha(theme.palette.info.main, 0.7), '&:hover': { color: theme.palette.info.main } }}>
                <CameraAlt fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {onOpenActionsGuide && (
            <Tooltip title="Edit, re-crop & action reference">
              <IconButton size="small" onClick={onOpenActionsGuide}
                sx={{ color: alpha(theme.palette.info.main, 0.7), '&:hover': { color: theme.palette.info.main } }}>
                <TouchApp fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Box>
  );
};
